import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import type { TeamsChatSummary, TeamsPerson } from '@atlas/shared';

import { api } from '../../services/api';
import { TeamsCloseIcon, TeamsSearchIcon } from '@atlas/ui';
import { useTeamsStatus } from '../../hooks/useTeamsChatSession';
import { TeamsAvatar } from './TeamsAvatar';
import { useTeamsPeople } from './useTeamsPeople';

const LISTBOX_ID = 'teams-person-picker-listbox';
const optionId = (index: number) => `teams-person-picker-option-${index}`;

/**
 * Where the directory starts answering — the server's own floor, restated.
 *
 * Duplicated rather than imported because it is a backend constant and the only
 * thing the client does with it is decide whether to say "keep typing". If the
 * two ever drift the cost is one slightly early or late hint, not a wrong result.
 */
const DIRECTORY_MIN_QUERY = 2;

export interface TeamsPersonPickerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the conversation the server started or reopened. The caller
   * selects it — the picker deliberately does not touch the store itself, so
   * the drawer and the standalone page each stay in charge of their own
   * navigation.
   */
  onChatCreated: (chat: TeamsChatSummary) => void;
}

/**
 * "New chat" — pick a person, land in the conversation.
 *
 * ## Two lists, and the wording follows which one you get
 *
 * People come from the user's own Teams chats and, when their token carries
 * `User.ReadBasic.All`, from the company directory as well. That scope was
 * consented on 2026-07-23 but does not attach to a token already issued, so a
 * user who connected earlier still gets chats only — and for them this dialog
 * must go on saying "people you've chatted with", because that is the truth of
 * what it can offer. Every user-visible string here therefore has two forms,
 * chosen by `directoryAvailable`.
 *
 * Getting this wrong in either direction is worse than it sounds. Promising a
 * directory search that cannot happen sends people hunting for a colleague who
 * was never going to appear; describing a real directory search as "people
 * you've chatted with" makes them stop looking before they have found anyone.
 *
 * ## Why a Dialog rather than a Popover
 *
 * The panel floors at 280px. A popover anchored inside it inherits that width
 * and leaves no room for a name, an email and an avatar on one row. A dialog is
 * portalled to the body, so it is sized by the viewport instead — the panel can
 * be as narrow as it likes and the picker stays usable.
 */
export function TeamsPersonPicker({ open, onClose, onChatCreated }: TeamsPersonPickerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Shares the cached status query the panel already runs — no extra request,
  // and gating it on `open` keeps a closed picker free.
  const { directoryAvailable, avatarsAvailable } = useTeamsStatus(open);

  const { people, isLoading, errorMessage, isDebouncing } = useTeamsPeople({
    query,
    enabled: open,
  });

  /**
   * Index of the first directory result, or -1.
   *
   * The list stays FLAT — the highlight and the keyboard cursor walk one array,
   * and the grouping is drawn on top by emitting a heading before this index.
   * Splitting into two arrays would mean two index spaces and arrow keys that
   * skip or stall at the boundary.
   */
  const firstDirectoryIndex = useMemo(
    () => people.findIndex((person) => person.source === 'directory'),
    [people],
  );
  /**
   * Headings are drawn only when there is a boundary to mark — both groups
   * present. A lone "People you've chatted with" over the only list there is
   * adds a line and says nothing.
   */
  const showGroupHeadings =
    directoryAvailable && firstDirectoryIndex > 0 && people.length > firstDirectoryIndex;

  /** Long enough for the server to have searched the directory with it. */
  const isQueryLongEnough = query.trim().length >= DIRECTORY_MIN_QUERY;

  const createChat = useMutation({
    mutationFn: (userId: string) => api.teams.createChat(userId),
    onSuccess: (chat) => {
      // A conversation that did not exist a moment ago has to appear in the
      // list, otherwise going "back to all conversations" loses it.
      void queryClient.invalidateQueries({ queryKey: ['teams', 'chats'] });
      onChatCreated(chat);
      // `onClose` directly rather than the guarded `handleClose`: the guard
      // exists to stop a close DURING a create, and this is the create landing.
      onClose();
    },
    retry: false,
  });

  const isCreating = createChat.isPending;

  /**
   * Reset on open, not on close.
   *
   * Clearing at close would wipe the query in the same frame the dialog starts
   * its exit transition, so the user watches their text vanish. It also has to
   * be *on open* rather than at mount: `keepMounted` is not used here, but the
   * mutation's error state outlives a cancelled close either way.
   */
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlightedIndex(0);
    createChat.reset();
    // Keyed on `open` alone, deliberately: `createChat` is a fresh object each
    // render, so depending on it would reset the mutation on every keystroke
    // and wipe the error the user is reading.
  }, [open]);

  // A shorter result list must not leave the highlight pointing past the end.
  useEffect(() => {
    setHighlightedIndex((current) => (current >= people.length ? 0 : current));
  }, [people.length]);

  const handleClose = useCallback(() => {
    // Never yank the dialog out from under an in-flight create: the success
    // handler is what selects the chat, and closing first would strand it.
    if (createChat.isPending) return;
    onClose();
  }, [createChat.isPending, onClose]);

  const pick = useCallback(
    (person: TeamsPerson) => {
      if (createChat.isPending) return;
      createChat.mutate(person.userId);
    },
    [createChat],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        // Stop it here rather than letting the Dialog's own handler run, so the
        // close goes through the guard above while a create is in flight.
        event.preventDefault();
        event.stopPropagation();
        handleClose();
        return;
      }
      if (people.length === 0 || isCreating) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((current) => (current + 1) % people.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((current) => (current - 1 + people.length) % people.length);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const person = people[highlightedIndex];
        if (person) pick(person);
      }
    },
    [handleClose, highlightedIndex, isCreating, people, pick],
  );

  const createErrorMessage = useMemo(() => {
    if (!createChat.error) return undefined;
    return createChat.error instanceof Error ? createChat.error.message : String(createChat.error);
  }, [createChat.error]);

  const isBusy = isLoading || isDebouncing;
  const showEmpty = !isBusy && !errorMessage && people.length === 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="teams-person-picker-title"
      slotProps={{
        paper: {
          // The dialog is portalled to the body, so the 280px panel floor does
          // not apply — but a phone viewport does.
          sx: { maxWidth: 'min(420px, calc(100vw - 32px))' },
        },
      }}
    >
      <DialogTitle
        id="teams-person-picker-title"
        sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, flex: 1 }}>
          {t('teams.panel.newChatTitle')}
        </Typography>
        {/*
          Escape and a backdrop click already closed this, and both still do.
          Neither is VISIBLE, though — the dialog opens with the cursor in a
          search box, and someone who changes their mind has nothing on screen
          telling them how to back out. An X is the affordance people look for,
          and its absence reads as being stuck in a modal.

          Disabled mid-create for the same reason the other exits are: closing
          while the chat is being created abandons a request whose result the
          caller is waiting to navigate to.
        */}
        <IconButton
          size="small"
          edge="end"
          onClick={handleClose}
          disabled={isCreating}
          aria-label={t('teams.panel.newChatCancelAriaLabel')}
        >
          <TeamsCloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      {/*
        `pt: 2`, not 0. The field autofocuses, so its label is ALWAYS in the
        floated position, sitting ~9px above the input's top border — and with
        no top padding the dialog content clipped it in half, leaving "Find
        someone you've chatted with" struck through by the outline. Measured in
        the browser: 8px still overhangs by 1px, 12px clears it. 16px (`2`) is
        the theme step that clears it with room.
      */}
      <DialogContent sx={{ pt: 2, pb: 2, minWidth: 0 }} onKeyDown={handleKeyDown}>
        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          size="small"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlightedIndex(0);
            // The stale failure belongs to a person they have moved on from.
            if (createChat.isError) createChat.reset();
          }}
          // Says what the list actually contains — which depends on whether
          // this token can reach the directory.
          label={t(
            directoryAvailable
              ? 'teams.panel.newChatSearchLabelDirectory'
              : 'teams.panel.newChatSearchLabel',
          )}
          placeholder={t('teams.panel.newChatSearchPlaceholder')}
          disabled={isCreating}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <TeamsSearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: {
              role: 'combobox',
              'aria-expanded': people.length > 0,
              'aria-controls': LISTBOX_ID,
              'aria-autocomplete': 'list',
              'aria-activedescendant': people.length > 0 ? optionId(highlightedIndex) : undefined,
            },
          }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {/*
            With the directory reachable this line has a job beyond describing
            the scope: it tells the user the company-wide half needs a couple of
            characters. Without it, an empty picker showing four recent people
            looks like the whole answer.
          */}
          {directoryAvailable
            ? t(
                isQueryLongEnough
                  ? 'teams.panel.newChatScopeHintDirectory'
                  : 'teams.panel.newChatScopeHintDirectoryShort',
              )
            : t('teams.panel.newChatScopeHint')}
        </Typography>

        {/* Determinate progress would be a lie — neither Graph call reports how
            far along it is. */}
        {isCreating ? <LinearProgress sx={{ mt: 1 }} /> : null}

        {createErrorMessage ? (
          <Alert severity="error" sx={{ mt: 1, '& .MuiAlert-message': { minWidth: 0 } }}>
            <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
              {t('teams.panel.newChatFailed', { message: createErrorMessage })}
            </Typography>
          </Alert>
        ) : null}

        {errorMessage ? (
          <Alert severity="error" sx={{ mt: 1, '& .MuiAlert-message': { minWidth: 0 } }}>
            <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
              {errorMessage}
            </Typography>
          </Alert>
        ) : null}

        <Box sx={{ mt: 1, minHeight: 120, maxHeight: 320, overflowY: 'auto', minWidth: 0 }}>
          {isBusy ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} aria-label={t('teams.panel.loading')} />
            </Box>
          ) : showEmpty ? (
            <Box sx={{ py: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('teams.panel.newChatNoResults')}
              </Typography>
              {/*
                Two different nothings. Without the directory this is a
                permission boundary rather than a failed search, and the user
                needs telling where to go instead. With it, the search really
                did cover the company — so the useful advice is about the query,
                not about Teams.
              */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t(
                  directoryAvailable
                    ? 'teams.panel.newChatNoResultsHintDirectory'
                    : 'teams.panel.newChatNoResultsHint',
                )}
              </Typography>
            </Box>
          ) : (
            <List
              id={LISTBOX_ID}
              role="listbox"
              aria-label={t(
                directoryAvailable
                  ? 'teams.panel.newChatResultsLabelDirectory'
                  : 'teams.panel.newChatResultsLabel',
              )}
              dense
              disablePadding
            >
              {people.map((person, index) => (
                <Fragment key={person.userId}>
                  {/*
                    `role="presentation"` because a listbox's children must be
                    options — an unlabelled `li` in between is read out as an
                    empty one by a screen reader.
                  */}
                  {showGroupHeadings && index === 0 ? (
                    <ListSubheader role="presentation" disableSticky sx={{ px: 1, lineHeight: 2 }}>
                      {t('teams.panel.newChatGroupChats')}
                    </ListSubheader>
                  ) : null}
                  {showGroupHeadings && index === firstDirectoryIndex ? (
                    <ListSubheader role="presentation" disableSticky sx={{ px: 1, lineHeight: 2 }}>
                      {t('teams.panel.newChatGroupDirectory')}
                    </ListSubheader>
                  ) : null}
                  <ListItemButton
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    selected={index === highlightedIndex}
                    disabled={isCreating}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => pick(person)}
                    sx={{ borderRadius: 1, minWidth: 0 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <TeamsAvatar
                        name={person.displayName}
                        entraOid={person.userId}
                        avatarsAvailable={avatarsAvailable}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={person.displayName}
                      secondary={person.email}
                      slotProps={{
                        primary: { noWrap: true, variant: 'body2' },
                        secondary: { noWrap: true, variant: 'caption' },
                      }}
                      sx={{ minWidth: 0, my: 0 }}
                    />
                  </ListItemButton>
                </Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
